import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoAwesome as AutoAwesomeIcon, Forum as ForumIcon } from '@mui/icons-material';
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from './shared';

export const INPUT_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'pdf', label: 'PDF' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'binary', label: 'Any file' },
  { value: 'json', label: 'JSON' },
];

const EXAMPLES = [
  {
    label: 'Meal photo → calories',
    inputType: 'image',
    prompt: 'Look at a photo of a meal and list each food with its estimated portion size and calories.',
  },
  {
    label: 'Invoice → key details',
    inputType: 'pdf',
    prompt: 'Read an invoice and pull out the invoice number, date, supplier name, line items and total amount.',
  },
  {
    label: 'Sort customer emails',
    inputType: 'text',
    prompt: 'Read a customer email, sort it into billing, technical problem or other, and write a one-line summary.',
  },
];

interface CreateWithAIHeroProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  inputType: string;
  onInputTypeChange: (value: string) => void;
  onCreate: () => void;
  busy: boolean;
  error: string | null;
  onSetUpYourself: () => void;
}

/** Top of the builder for a new function: describe it in plain words and AI fills in the form. */
export const CreateWithAIHero: React.FC<CreateWithAIHeroProps> = ({
  prompt,
  onPromptChange,
  inputType,
  onInputTypeChange,
  onCreate,
  busy,
  error,
  onSetUpYourself,
}) => {
  const canCreate = !!prompt.trim() && !busy;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="create-with-ai-title"
      sx={(theme) => ({
        mb: 3,
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 2,
        borderColor: alpha(theme.palette.primary.main, 0.35),
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      })}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Typography id="create-with-ai-title" variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          What should this function do?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Describe it in plain words. AI writes the instructions and designs what comes back, then fills in the form
          for you to check. Nothing is saved until you click Save.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography id="create-input-type-label" variant="body2" sx={{ fontWeight: 600 }}>
            It takes in
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={inputType}
            onChange={(_, value: string | null) => value && onInputTypeChange(value)}
            aria-labelledby="create-input-type-label"
            disabled={busy}
            sx={{ flexWrap: 'wrap', bgcolor: 'background.paper' }}
          >
            {INPUT_TYPES.map((t) => (
              <ToggleButton key={t.value} value={t.value} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          label="Describe what it should do"
          placeholder="E.g. Read a product label photo and return the brand, product name and ingredients"
          value={prompt}
          disabled={busy}
          onChange={(e) => onPromptChange(e.target.value)}
          onKeyDown={(e) => {
            if (isSubmitShortcut(e) && canCreate) {
              e.preventDefault();
              onCreate();
            }
          }}
          helperText="Mention what comes in, what you want back, and any rules (units, language, things to ignore)."
          sx={{ bgcolor: 'background.paper' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Try an example:
          </Typography>
          {EXAMPLES.map((ex) => (
            <Chip
              key={ex.label}
              label={ex.label}
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => {
                onInputTypeChange(ex.inputType);
                onPromptChange(ex.prompt);
              }}
            />
          ))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2.5 }}>
          <Button
            variant="contained"
            size="large"
            onClick={onCreate}
            disabled={!canCreate}
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          >
            {busy ? 'Creating…' : 'Create with AI'}
          </Button>
          <Typography variant="caption" color="text.secondary" aria-live="polite">
            {busy ? 'This can take up to a minute.' : `or press ${SUBMIT_SHORTCUT_LABEL}`}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button size="small" onClick={onSetUpYourself} disabled={busy} sx={{ textTransform: 'none' }}>
            I'd rather set it up myself
          </Button>
          <Link
            component={RouterLink}
            to="/dashboard/guide"
            variant="body2"
            underline="hover"
            color="text.secondary"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <ForumIcon sx={{ fontSize: 16 }} />
            Not sure what to build? Ask the Build Guide
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default CreateWithAIHero;
